import { useEffect, useState } from "react";
import {
    Avatar,
    Badge,
    Table,
    Text,
    VStack,
    HStack,
    Menu,
    IconButton
} from "@chakra-ui/react";
import {
    EllipsisVertical,
    FileText,
    CarTaxiFront,
    Eye,
    IdCard,
} from "lucide-react";
import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    registerDriverForUserAction,
    registerVehicleForUserAction,
    fetchRecruitedVehiclesAction,
} from "../../../state/actions/driverActions";
import DataTable from "../../../components/DataTable";
import PartnerMilestone from "./PartnerMilestone";
import AddDriverDocumentsDrawer from "./AddDriverDocumentsDrawer";
import AddDriverVehicleDrawer from "./AddDriverVehicleDrawer";
import {
    fetchReferredDriversAction,
} from "../../../state/actions/driverPartnerActions";
import {toaster} from "../../../components/toaster";
import ActivityToast from "../../../components/ActivityToast";
import ViewDriverDrawer from "./ViewDriverDrawer";
import ViewVehicleDrawer from "./ViewVehicleDrawer";

const badgeColor = {
    Approved: "green",
    Pending: "yellow",
    Active: "blue",
    Inactive: "yellow",
    Suspended: "red",
    Rejected: "red",
};

const columns = [
    {
        key: "partner",
        label: "Driver",
    },
    {
        key: "joined",
        label: "Date Registered",
    },
    {
        key: "status",
        label: "Driver Registered",
    },
    {
        key: "milestone",
        label: "Milestone Progress",
    },
    {
        key: "earnings",
        label: "Earned",
        align: "end",
    },
     {
        key: "actions",
        label: "Actions",
        align: "end",
    },
];

const PartnersTable = ({
    drivers = [],
    milestones = [],
}) => {
    const [
    selectedDriver,
    setSelectedDriver,
] = useState(
    null
);
const dispatch =
    useDispatch();
const {
    drivers: driverDetails,
    registeringDriver,
    registeringVehicle,
    recruitedVehicles,
} = useSelector(
    state => state.driver
);

useEffect(() => {
    dispatch(
        fetchRecruitedVehiclesAction(
            1,
            50
        )
    );
}, [
    dispatch,
]);
    
        const [
            isDriverOpen,
            setDriverOpen,
        ] = useState(false);
        const [
            isDocumentsOpen,
            setDocumentsOpen,
        ] = useState(false);
        const [
    isVehicleOpen,
    setVehicleOpen,
] = useState(false);

const [
    isVehicleDetailsOpen,
    setVehicleDetailsOpen,
] = useState(false);

const [
    selectedVehicle,
    setSelectedVehicle,
] = useState(null);



const handleRegisterDriver =
    async (
        driver,
        form
    ) => {

        const result =
            await dispatch(
                registerDriverForUserAction(
                    driver.user_id,
                    form
                )
            );

        if (
            result.success
        ) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Driver Registered"
                        description="The driver profile and documents were submitted successfully."
                        timestamp="Just now"
                    />
                ),
            });
            dispatch(
            fetchReferredDriversAction(
                1,
                10
            )
        );

            setDocumentsOpen(
                false
            );
        } else {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Registration Failed"
                        description={
                            result.error ||
                            "Unable to register driver."
                        }
                        timestamp="Just now"
                    />
                ),
            });
        }

        return result;
    };

const handleRegisterVehicle =
    async (
        driver,
        form
    ) => {

        const result =
            await dispatch(
                registerVehicleForUserAction(
                    driver.user_id,
                    form
                )
            );

        if (
            result.success
        ) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Vehicle Registered"
                        description="The vehicle details and supporting documents were submitted successfully."
                        timestamp="Just now"
                    />
                ),
            });
             dispatch(
            fetchReferredDriversAction(
                1,
                10
            )
        );
            setVehicleOpen(
                false
            );
        } else {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Registration Failed"
                        description={
                            result.error ||
                            "Unable to register vehicle."
                        }
                        timestamp="Just now"
                    />
                ),
            });
        }

        return result;
    };

   const partners =
    drivers.map(
        (
            referredDriver
        ) => {

            const fullDriver =
                referredDriver
                    .driver
                    ?.driver_id
                    ? driverDetails?.[
                          referredDriver
                              .driver
                              .driver_id
                      ]
                    : null;
            const status=
    fullDriver?.status ||
    referredDriver?.driver?.status ||
    "Pending";
            const driver =
                fullDriver ||
                referredDriver;

            const user =
                fullDriver?.user ||
                referredDriver;

            const driverId =
                fullDriver
                    ?.driver_id ||
                referredDriver
                    ?.driver
                    ?.driver_id;

            const hasDriver =
        !!(
            fullDriver?.driver_id ||
            referredDriver?.driver
                ?.driver_id
        );

       const hasVehicle =
    (recruitedVehicles || []).some(
        vehicle =>
            vehicle?.user?.user_id ===
            user?.user_id
    );
            const driverMilestones =
                milestones.filter(
                    (
                        milestone
                    ) =>
                        milestone.driver_id ===
                        driverId
                );

            const achievedMilestones =
                driverMilestones.filter(
                    (
                        milestone
                    ) =>
                        milestone.milestone_status ===
                        "Achieved"
                );

            const latestMilestone =
                achievedMilestones[
                    achievedMilestones.length -
                        1
                ];

            const totalEarned =
                achievedMilestones.reduce(
                    (
                        sum,
                        milestone
                    ) =>
                        sum +
                        Number(
                            milestone.commission_amount ||
                                0
                        ),
                    0
                );

            const progress =
                driverMilestones.length >
                0
                    ? Math.round(
                          (
                              achievedMilestones.length /
                              driverMilestones.length
                          ) *
                              100
                      )
                    : 0;

            return {
                id:
                    user.reference ||
                    driverId ||
                    user.user_id,

                driver,
                hasDriver,
                hasVehicle,
                name:
                    [
                        user.first_name,
                        user.last_name,
                    ]
                        .filter(
                            Boolean
                        )
                        .join(
                            " "
                        ) ||
                    user.username ||
                    "Unknown Driver",

                joined:
                    new Date(
                        fullDriver
                            ?.created_at ||
                            referredDriver.created_at
                    ).toLocaleDateString(),

                status:
    status,

driverStatus:
    status,

                userStatus:
                    referredDriver.status,

                milestone:
                    latestMilestone
                        ?.milestone_type ||
                    "Registration Started",

                progress,

                progressLabel: `${achievedMilestones.length}/${driverMilestones.length}`,

                earnings: `P${totalEarned.toFixed(
                    2
                )}`,
            };
        }
    );


 return (
        <>
        <DataTable
            title="Recruited Drivers"
            total={
                partners.length
            }
            data={partners}
            columns={columns}
            pageSize={5}
            filters={[
                "All",
                "Pending",
                "Approved",
                "Active",
                "Inactive",
                "Suspended",
            ]}
            defaultFilter="All"
            renderRow={(
                partner
            ) => (
                <Table.Row
                    key={
                        partner.id
                    }
                    cursor="pointer"
                    _hover={{
                        bg: "gray.50",
                    }}
                   onClick={() => {

                    const fullDriver =
                        partner.driver.driver?.driver_id
                            ? driverDetails[
                                partner.driver.driver.driver_id
                            ]
                            : partner.driver;

                    setSelectedDriver(
                        fullDriver
                    );

                    setDriverOpen(
                        true
                    );
                }}
                    >
                    <Table.Cell>
                        <HStack>
                            <Avatar.Root
                                bg="#E8EEFF"
                                color="#2453FF"
                            >
                                <Avatar.Fallback>
                                    {partner.name
                                        .split(
                                            " "
                                        )
                                        .map(
                                            (
                                                n
                                            ) =>
                                                n[0]
                                        )
                                        .join(
                                            ""
                                        )}
                                </Avatar.Fallback>
                            </Avatar.Root>

                            <VStack
                                align="start"
                                gap={0}
                            >
                                <Text fontWeight="600">
                                    {
                                        partner.name
                                    }
                                </Text>

                                <Text
                                    fontSize="sm"
                                    color="gray.500"
                                >
                                    ID:{" "}
                                    {
                                        partner.id
                                    }
                                </Text>
                            </VStack>
                        </HStack>
                    </Table.Cell>

                    <Table.Cell>
                        {
                            partner.joined
                        }
                    </Table.Cell>

                   <Table.Cell>
    <Badge
        colorPalette={
            partner.driverStatus
                ? badgeColor[
                      partner.driverStatus
                  ] || "gray"
                : "gray"
        }
        borderRadius="full"
        px={3}
    >
        {
            partner.driverStatus ||
            "Not Registered"
        }
    </Badge>
</Table.Cell>

                    <Table.Cell>
                        <PartnerMilestone
                            title={
                                partner.milestone
                            }
                            progress={
                                partner.progress
                            }
                            label={
                                partner.progressLabel
                            }
                        />
                    </Table.Cell>

                    <Table.Cell textAlign="end">
                        <Text
                            fontWeight="700"
                            color={
                                partner.earnings !==
                                "P0.00"
                                    ? "#0F766E"
                                    : "gray.600"
                            }
                        >
                            {
                                partner.earnings
                            }
                        </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="end">
    <Menu.Root>
        <Menu.Trigger asChild>
            <IconButton
                variant="ghost"
                size="sm"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <EllipsisVertical size={18} color="#64748B" />
            </IconButton>
        </Menu.Trigger>

        <Menu.Positioner>
              <Menu.Content>

    {!partner.hasDriver && (
        <Menu.Item
            value="upload-documents"
            onClick={(e) => {
                e.stopPropagation();

                setSelectedDriver(
                    partner.driver
                );

                setDocumentsOpen(
                    true
                );
            }}
        >
            <FileText size={16} />
            Upload Driver Documents
        </Menu.Item>
    )}

    {partner.hasDriver &&
        !partner.hasVehicle && (
        <Menu.Item
            value="add-vehicle"
            onClick={(e) => {
                e.stopPropagation();

                setSelectedDriver(
                    partner.driver
                );

                setVehicleOpen(
                    true
                );
            }}
        >
            <CarTaxiFront size={16} />
            Add Driver Vehicle
        </Menu.Item>
    )}

    {partner.hasDriver && (
        <Menu.Item
            value="view-driver"
            onClick={(e) => {
                e.stopPropagation();

                setSelectedDriver(
                    partner.driver
                );

                setDriverOpen(
                    true
                );
            }}
        >
            <IdCard size={16} />
            View Driver Details
        </Menu.Item>
    )}

    {partner.hasVehicle && (
        <Menu.Item
            value="view-vehicle"
            onClick={(e) => {
                e.stopPropagation();

                const driverUserId =
                    partner.driver?.user_id ||
                    partner.driver?.user?.user_id;

                const vehicleRecord =
                    recruitedVehicles.find(
                        item =>
                            item.user.user_id ===
                            driverUserId
                    );

                if (
                    !vehicleRecord
                ) {
                    return;
                }

                setSelectedVehicle(
                    vehicleRecord.vehicle
                );

                setVehicleDetailsOpen(
                    true
                );
            }}
        >
            <Eye size={16} />
            View Vehicle Details
        </Menu.Item>
    )}

</Menu.Content>
        </Menu.Positioner>
    </Menu.Root>
</Table.Cell>
                </Table.Row>
                
            )}
        />
        <ViewDriverDrawer
            open={isDriverOpen}
            onClose={() =>
                setDriverOpen(
                    false
                )
            }
            driver={
                selectedDriver
            }
        />
            <AddDriverDocumentsDrawer
                open={isDocumentsOpen}
                onClose={() =>
                    setDocumentsOpen(
                        false
                    )
                }
                driver={
                    selectedDriver
                }
                loading={
                    registeringDriver
                }
                onSubmit={
                    handleRegisterDriver
                }
            />

            <AddDriverVehicleDrawer
                open={isVehicleOpen}
                onClose={() =>
                    setVehicleOpen(
                        false
                    )
                }
                driver={
                    selectedDriver
                }
                loading={
                    registeringVehicle
                }
                onSubmit={
                    handleRegisterVehicle
                }
            />
            <ViewVehicleDrawer
    open={isVehicleDetailsOpen}
    onClose={() =>
        setVehicleDetailsOpen(
            false
        )
    }
    vehicle={
        selectedVehicle
    }
/>
     </>
    );
};

export default PartnersTable;